// Importing necessary components and styles
import Checkbox from "../components/Checkbox";
import { colors } from "../config";
import { H2, INTERPRETATION } from "../components/Text";
import Button from "../components/Button";
import ColorWheel from "../components/ColorWheel";
import Logo from "../images/Pronouns compass/Svgs/Logo";
import "./Question.css";
import Menu from "../components/Menu";
import { useState } from "react";

// Functional component representing a question in the questionnaire
const Question = ({
  schema,
  data,
  setData,
  submit,
  previous,
  isLast = false,
}) => {
  const [purple, setPurple] = useState(true);
  const [leftPurple, setLeftPurple] = useState(false);
  const [rightPurple, setRightPurple] = useState(false);
  // Function to toggle color selection based on user interaction
  const toggleColor = (key) => {
    // Creating a copy of the current data to avoid mutation
    const newData = { ...data };
    // Toggling the selected color
    newData.colors[key] = !newData.colors[key];
    newData.ratherNot = false;
    // If the question allows multiple answers
    if (schema.multi) {
      if (key === "na" && newData.colors["na"]) {
        newData.colors["na"] = true;
        newData.colors["she"] = false;
        newData.colors["they"] = false;
        newData.colors["he"] = false;
        newData.colors["other"] = false;
      } else if (key === "na" && !newData.colors["na"]) {
        newData.colors["na"] = false;
      } else if (
        key === "she" ||
        key === "they" ||
        key === "he" ||
        key === "other"
      ) {
        newData.colors["na"] = false;
      }
    } else {
      Object.keys(newData.colors).forEach((colorKey) => {
        if (colorKey !== key) {
          newData.colors[colorKey] = false;
        }
      });
    }
    if (schema.neighbours) {
      if (
        (key === "red" || key === "blue" || key === "yellow") &&
        newData.colors["red"] &&
        newData.colors["blue"] &&
        newData.colors["yellow"] &&
        purple === true
      ) {
        newData.colors["rainbow"] = true;
        newData.colors["purple"] = true;
        newData.colors["red"] = true;
        newData.colors["blue"] = true;
        newData.colors["yellow"] = true;
      }
    }
    // Handling specific color combinations based on user selections
    if (key === "red") {
      setLeftPurple(true);
    }
    if (key === "blue") {
      setRightPurple(true);
    }
    if (key === "purple" && !newData.colors[key]) {
      setPurple(false);
      setLeftPurple(false);
      setRightPurple(false);
    }
    if (key === "purple" && newData.colors[key]) {
      setPurple(true);
    }
    if (
      key === "purple" &&
      newData.colors[key] &&
      newData.colors["red"] &&
      newData.colors["blue"] &&
      newData.colors["yellow"]
    ) {
      newData.colors["rainbow"] = true;
    } else if (
      (key === "red" || key === "blue") &&
      newData.colors["red"] &&
      newData.colors["blue"] &&
      purple
    ) {
      newData.colors["purple"] = true;
    } else if (
      (key === "red" && !newData.colors["red"]) ||
      (key === "blue" && !newData.colors["blue"])
    ) {
      newData.colors["purple"] = false;
    } else if (key === "rainbow" && newData.colors[key]) {
      // If rainbow is selected, turn on all colors and turn off grey
      for (let k of Object.keys(newData.colors)) {
        newData.colors[k] = true;
      }
      newData.colors["grey"] = false;
    } else if (key === "rainbow" && !newData.colors[key]) {
      // If rainbow is deselected, turn off all colors
      for (let k of Object.keys(newData.colors)) {
        newData.colors[k] = false;
      }
    } else if (key === "grey" && newData.colors[key]) {
      // If grey is selected, turn off all colors
      for (let k of Object.keys(newData.colors)) {
        newData.colors[k] = false;
      }
      newData.colors["grey"] = true;
    }
    if (schema.neighbours) {
      if (
        (key === "red" || key === "blue" || key === "yellow") &&
        newData.colors["red"] &&
        newData.colors["blue"] &&
        newData.colors["yellow"] &&
        leftPurple &&
        rightPurple
      ) {
        newData.colors["rainbow"] = true;
        newData.colors["purple"] = true;
        newData.colors["red"] = true;
        newData.colors["blue"] = true;
        newData.colors["yellow"] = true;
      }
    }

    // Turning off rainbow if any color is deselected
    if (key !== "rainbow" && !newData.colors[key]) {
      newData.colors["rainbow"] = false;
    }

    // Turning off grey if any color other than grey is selected
    if (key !== "grey") {
      newData.colors["grey"] = false;
    }
    // If only one answer is allowed.

    // Updating the state with the new data
    setData(newData);
  };

  // Function to toggle the selected suffix
  const toggleSuffix = (key) => {
    const old = data.suffix[key];
    const newData = { ...data };

    // Turning off all other suffix options and toggling the selected one
    for (let k of Object.keys(newData.suffix)) {
      newData.suffix[k] = false;
    }
    newData.suffix[key] = !old;

    // Updating the state with the new data
    setData(newData);
  };

  const toggleRatherNot = () => {
    const newData = { ...data };

    if (newData["ratherNot"] === false) {
      Object.keys(newData.colors).forEach((colorKey) => {
        newData.colors[colorKey] = false;
      });
      newData["ratherNot"] = true;
    } else {
      newData["ratherNot"] = false;
    }
    setData(newData);
  };

  // Calculating submittability based on user responses
  const submittable =
    data.ratherNot ||
    (Object.keys(data.colors).some((key) => data.colors[key]) &&
      (!schema.suffix ||
        Object.keys(data.suffix).some((key) => data.suffix[key])));

  // Rendering the question and its options
  return (
    <div style={{ maxHeight: "100vh", minHeight: "900px" }}>
      <div style={{ paddingTop: "10px" }}>
        <Logo width={"100%"} height={"50%"} id={"logo"} />
      </div>
      {/* Displaying the prompt for the current question */}
      <H2>{schema.prompt}</H2>

      {/* Rendering the color selection options */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Rendering checkboxes for each color option */}
        <div
          className={schema.prompt === "I am polyamorous" ? "polyClass" : ""}
        >
          {Object.keys(schema.options).map((key) => (
            <div className="checkbox">
              <Checkbox
                label={schema.options[key]}
                key={key}
                checked={data.colors[key]}
                onChange={() => toggleColor(key)}
                colors={{ primary: colors[key][0], secondary: colors[key][1] }}
              />
            </div>
          ))}
        </div>

        {/* Rendering the ColorWheel based on selected colors */}

        {(schema.prompt === "I am polyamorous" && data.ratherNot === true) ||
        (schema.prompt === "I am polyamorous" &&
          data.colors["grey"] === false &&
          data.colors["grey_2"] === false) ? (
          <div
            style={
              data.ratherNot === true ? { opacity: "10%" } : { opacity: "100%" }
            }
          >
            <ColorWheel
              selected={["grey_2"]}
              isSymbol={schema.usesSymbol}
              neighbours={schema.neighbours}
            />
          </div>
        ) : (
          <div>
            <ColorWheel
              selected={Object.keys(data.colors).filter(
                (key) => data.colors[key]
              )}
              isSymbol={schema.usesSymbol}
              neighbours={schema.neighbours}
              leftPurple={leftPurple}
              rightPurple={rightPurple}
            />
          </div>
        )}
      </div>

      {/* Rendering additional suffix options if provided in the schema */}
      <div
        style={{
          marginTop: "50px",
          textAlign: "center",
          marginBottom: "25px",
        }}
      >
        {schema.suffix && (
          <div>
            {Object.keys(schema.suffix).map((key) => (
              <Checkbox
                inline={true}
                label={schema.suffix[key]}
                key={key}
                checked={data.suffix[key]}
                onChange={() => toggleSuffix(key)}
                colors={{ primary: colors[key][0], secondary: colors[key][1] }}
              />
            ))}
          </div>
        )}

        {/* Rendering a checkbox for "I'd rather not answer this question" */}
        <div style={{ marginTop: "50px" }}>
          <Checkbox
            label="I'd rather not answer this question"
            checked={data.ratherNot}
            onChange={() => toggleRatherNot()}
            colors={{
              secondary: colors["dark_grey"][1],
              primary: colors["dark_grey"][0],
            }}
          />
        </div>
        <div
          className="button-container"
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            paddingBottom: "20px",
          }}
        >
          {/* button that returns user to previous question*/}
          <div
            onClick={() => {
              previous();
            }}
          >
            <Button
              enabled={true.toString()}
              type="back"
              funct={previous}
            ></Button>
          </div>

          {/* Rendering the submit button based on submittability */}
          <div onClick={submittable ? submit : () => {}}>
            <Button type="next" enabled={submittable.toString()}></Button>
          </div>
        </div>
      </div>
      <Menu />
    </div>
  );
};

// Exporting the Question component as the default export
export default Question;
